<template>
  <div class="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-gap-2 tw-flex-wrap">
    <div class="tw-flex tw-items-center tw-gap-2">
      <FormField v-model="search" type="text" :hide-label="true" :hide-errors="true"
        :placeholder="$t('subscriptions.logs.search', 'Search by IP, event...')"
        left-icon="solar:magnifer-linear" @input="onSearch" class="tw-bg-white tw-rounded-md tw-min-w-[260px]" />
      <FormSelect v-model="status" :options="statusOptions" class="tw-bg-white tw-rounded-md tw-min-w-[150px]" />
    </div>
    <a-button intent="flat" left-icon="solar:refresh-bold-duotone" @click="load(pagination.current_page)" :loading="loading">
      {{ $t('common.refresh', 'Refresh') }}
    </a-button>
  </div>

  <div class="tw-bg-white tw-rounded-lg tw-shadow-smx tw-border tw-overflow-hidden tw-border-gray-200">
  <div class="tw-rounded tw-overflow-auto tw-border tw-border-solid tw-border-gray-100">
    <table class="tw-min-w-full tw-leading-normal">
      <thead>
        <tr>
          <th v-for="c in columns" :key="c.label" :class="c.classes"
            class="tw-px-5 last:tw-border-e-0 tw-whitespace-nowrap tw-border-e tw-py-3 tw-border-b tw-border-gray-100 tw-bg-gray-50 tw-text-start tw-text-[12px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider">
            {{ $t(c.label, c.fallback) }}
          </th>
        </tr>
      </thead>

      <tbody v-if="loading">
        <tr v-for="i in 10" :key="i" class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50">
          <td v-for="c in columns" :key="c.label" class="tw-px-2 tw-py-3">
            <div class="tw-w-full tw-py-3 tw-bg-gray-50 group-even:tw-bg-white tw-rounded tw-min-w-[30px] tw-animate-pulse"></div>
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <template v-if="items.length">
          <tr v-for="l in items" :key="l.id" class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50 tw-text-sm">
            <td class="tw-px-5 tw-py-3 tw-whitespace-nowrap">{{ formatDate(l.created_at) }}</td>
            <td class="tw-px-5 tw-py-3">{{ l.event || '—' }}</td>
            <td class="tw-px-5 tw-py-3 tw-font-mono tw-text-xs">{{ l.ip_address }}</td>
            <td class="tw-px-5 tw-py-3">
              <icon :icon="l.signature_valid ? 'solar:check-circle-bold' : 'solar:close-circle-bold'" :class="l.signature_valid ? 'tw-text-green-500' : 'tw-text-red-500'" class="tw-text-lg" />
            </td>
            <td class="tw-px-5 tw-py-3">
              <span :class="statusClass(l.status)" class="tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-capitalize">{{ l.status }}</span>
              <span class="tw-text-xs tw-text-gray-400 tw-ml-1">{{ l.http_status }}</span>
            </td>
            <td class="tw-px-5 tw-py-3 tw-text-end">
              <button @click="openDetail(l)" class="tw-text-primary-500 hover:tw-text-primary-600 tw-text-xs tw-font-semibold">{{ $t('common.view', 'View') }}</button>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr>
            <td :colspan="columns.length">
              <div class="tw-p-5 tw-bg-white tw-min-h-[250px] tw-text-gray-600 tw-flex tw-items-center tw-justify-center tw-text-lg">
                <p>{{ $t('subscriptions.logs.empty', 'No webhook requests logged yet.') }}</p>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
  </div>

  <div class="tw-mt-2">
    <TablePagination v-if="items.length" :has-next="!!pagination.next_page_url" :has-previous="!!pagination.prev_page_url" @previous="onPrevious" @next="onNext" />
  </div>

  <!-- Detail modal -->
  <transition enter-active-class="tw-duration-200" enter-from-class="tw-opacity-0" leave-to-class="tw-opacity-0">
    <div v-if="detail.open" class="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4" @click.self="detail.open = false">
      <div class="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-2xl tw-max-h-[85vh] tw-overflow-y-auto">
        <div class="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-sticky tw-top-0 tw-bg-white">
          <h3 class="tw-text-lg tw-font-bold tw-text-gray-800">{{ $t('subscriptions.logs.detail', 'Webhook request') }} #{{ detail.data.id }}</h3>
          <button @click="detail.open = false" class="tw-text-gray-400 hover:tw-text-gray-700"><icon icon="solar:close-circle-bold-duotone" class="tw-text-2xl" /></button>
        </div>
        <div class="tw-p-5 tw-text-sm tw-space-y-2">
          <p><span class="tw-font-semibold">{{ $t('subscriptions.logs.event', 'Event') }}:</span> {{ detail.data.event }}</p>
          <p><span class="tw-font-semibold">{{ $t('subscriptions.logs.message', 'Message') }}:</span> {{ detail.data.message || '—' }}</p>
          <p><span class="tw-font-semibold">URL:</span> <span class="tw-break-all">{{ detail.data.url }}</span></p>
          <div>
            <p class="tw-font-semibold tw-mb-1">{{ $t('subscriptions.logs.payload', 'Payload') }}</p>
            <pre class="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-xs tw-overflow-x-auto">{{ pretty(detail.data.payload) }}</pre>
          </div>
          <div>
            <p class="tw-font-semibold tw-mb-1">{{ $t('subscriptions.logs.headers', 'Headers') }}</p>
            <pre class="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-xs tw-overflow-x-auto">{{ pretty(detail.data.headers) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import TablePagination from '@/components/table/TablePagination';
import subscriptionsApi from '@/api/subscriptions';
import useAlert from '@/composables/useAlert';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const items = ref([]);
const loading = ref(false);
const status = ref('all');
const search = ref('');
const pagination = reactive({ current_page: 1, last_page: 1, total: 0, next_page_url: null, prev_page_url: null });
const detail = reactive({ open: false, data: {} });

const columns = [
  { label: 'subscriptions.logs.time', fallback: 'Time', classes: ['tw-w-[170px]'] },
  { label: 'subscriptions.logs.event', fallback: 'Event', classes: ['tw-w-[140px]'] },
  { label: 'subscriptions.logs.ip', fallback: 'IP', classes: ['tw-w-[140px]'] },
  { label: 'subscriptions.logs.signature', fallback: 'Signature', classes: ['tw-w-[100px]'] },
  { label: 'subscriptions.plans.status', fallback: 'Status', classes: ['tw-w-[130px]'] },
  { label: 'common.actions', fallback: 'Actions', classes: ['tw-w-[90px]'] },
];

const statusOptions = [
  { id: 'all', name: t('subscriptions.logs.all', 'All') },
  { id: 'processed', name: 'Processed' },
  { id: 'rejected', name: 'Rejected' },
  { id: 'failed', name: 'Failed' },
  { id: 'disabled', name: 'Disabled' },
];

const load = async (page = 1) => {
  loading.value = true;
  items.value = [];
  try {
    const res = await subscriptionsApi.admin.logs({ page, status: status.value, search: search.value || undefined });
    const d = res.data.data;
    items.value = d.data || [];
    Object.assign(pagination, { current_page: d.current_page, last_page: d.last_page, total: d.total, next_page_url: d.next_page_url, prev_page_url: d.prev_page_url });
  } catch (e) {
    useAlert(t('errors.something-wrong-happened'), 'danger');
  } finally {
    loading.value = false;
  }
};

const onNext = () => { if (pagination.current_page < pagination.last_page) load(pagination.current_page + 1); };
const onPrevious = () => { if (pagination.current_page > 1) load(pagination.current_page - 1); };

const openDetail = async (l) => {
  try { const res = await subscriptionsApi.admin.log(l.id); detail.data = res.data.data; detail.open = true; }
  catch (e) { useAlert(t('errors.something-wrong-happened'), 'danger'); }
};

const pretty = (v) => { try { return JSON.stringify(v, null, 2); } catch (e) { return String(v); } };
const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';
const statusClass = (s) => ({
  processed: 'tw-bg-green-100 tw-text-green-700',
  rejected: 'tw-bg-yellow-100 tw-text-yellow-700',
  failed: 'tw-bg-red-100 tw-text-red-600',
  disabled: 'tw-bg-gray-100 tw-text-gray-500',
}[s] || 'tw-bg-gray-100 tw-text-gray-500');

let searchTimer = null;
const onSearch = () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => load(1), 500); };

watch(status, () => load(1));
onMounted(() => load());
</script>
